import { TodoItem, Project } from './adhd-todo-manager';

export interface AutoPriorityConfig {
  urgencyFactors: {
    dueDateProximity: number;    // Weight for days until due date
    keywordIntensity: number;    // Weight for urgency keywords
    timeOfCreation: number;      // Weight for creation time urgency signals
    projectDeadline: number;     // Weight for project deadline proximity
  };
  
  importanceFactors: {
    projectImpact: number;       // Weight for project-level importance
    stakeholderKeywords: number; // Weight for stakeholder-related content
    longTermGoals: number;       // Weight for career/health/relationship keywords
    complexitySignals: number;   // Weight for task complexity indicators
  };
  
  adhdOptimizations: {
    energyMatching: boolean;     // Match task complexity to typical energy patterns
    hyperfocusGrouping: boolean; // Group similar context tasks
    overwhelmPrevention: boolean; // Limit Q1 tasks to prevent paralysis
    timeBlindnessCompensation: boolean; // Extra weight for time-related factors
  };
}

export interface PriorityAnalysis {
  urgencyScore: number;        // 1-10 scale
  importanceScore: number;     // 1-10 scale
  eisenhowerQuadrant: 1 | 2 | 3 | 4; // Q1: Do, Q2: Schedule, Q3: Delegate, Q4: Delete
  confidence: number;          // 0-100 confidence in the scoring
  reasoning: string[];         // Human-readable reasons for the score
  adhdFactors: {
    energyLevel: 'low' | 'medium' | 'high'; // Required energy level
    contextSwitchingCost: number; // Cognitive load of starting this task
    hyperfocusCompatible: boolean; // Suitable for hyperfocus sessions
  };
}

export class EisenhowerMatrix {
  private config: AutoPriorityConfig = {
    urgencyFactors: {
      dueDateProximity: 0.4,     // 40% weight
      keywordIntensity: 0.3,     // 30% weight  
      timeOfCreation: 0.1,       // 10% weight
      projectDeadline: 0.2       // 20% weight
    },
    importanceFactors: {
      projectImpact: 0.3,        // 30% weight
      stakeholderKeywords: 0.25, // 25% weight
      longTermGoals: 0.25,       // 25% weight
      complexitySignals: 0.2     // 20% weight
    },
    adhdOptimizations: {
      energyMatching: true,
      hyperfocusGrouping: true,
      overwhelmPrevention: true,
      timeBlindnessCompensation: true
    }
  };

  // Urgency keywords with intensity scores
  private urgencyKeywords = {
    critical: 10,
    urgent: 9,
    asap: 9,
    emergency: 10,
    deadline: 8,
    overdue: 10,
    today: 8,
    immediately: 9,
    rush: 7,
    quick: 6,
    fast: 6,
    soon: 5,
    priority: 6,
    important: 5
  };

  // Stakeholder keywords (high importance)
  private stakeholderKeywords = {
    boss: 9,
    manager: 9,
    client: 8,
    customer: 8,
    team: 6,
    meeting: 7,
    presentation: 7,
    review: 6,
    approval: 7,
    feedback: 5,
    followup: 5,
    'follow up': 5,
    'follow-up': 5
  };

  // Long-term goal keywords (high importance)
  private longTermKeywords = {
    career: 8,
    health: 9,
    exercise: 7,
    learning: 7,
    skill: 6,
    course: 6,
    certification: 7,
    relationship: 8,
    family: 8,
    personal: 5,
    growth: 6,
    development: 6,
    goal: 7,
    vision: 6,
    strategy: 7
  };

  // Complexity indicators (affects energy requirements)
  private complexityKeywords = {
    research: 7,
    analyze: 8,
    design: 7,
    plan: 6,
    create: 6,
    build: 7,
    implement: 8,
    debug: 9,
    troubleshoot: 8,
    optimize: 7,
    refactor: 6,
    document: 4,
    email: 2,
    call: 3,
    schedule: 2,
    organize: 4
  };

  constructor(customConfig?: Partial<AutoPriorityConfig>) {
    if (customConfig) {
      this.config = { ...this.config, ...customConfig };
    }
  }

  /**
   * Automatically analyze a todo item and assign Eisenhower Matrix priority
   */
  public analyzePriority(
    todo: TodoItem, 
    projects?: Project[], 
    currentTime: Date = new Date()
  ): PriorityAnalysis {
    const urgencyScore = this.calculateUrgencyScore(todo, projects, currentTime);
    const importanceScore = this.calculateImportanceScore(todo, projects);
    
    // Calculate Eisenhower quadrant based on scores
    // Thresholds: Urgent >= 6, Important >= 6
    const isUrgent = urgencyScore.score >= 6;
    const isImportant = importanceScore.score >= 6;
    
    let quadrant: 1 | 2 | 3 | 4;
    if (isUrgent && isImportant) quadrant = 1;      // Do First
    else if (!isUrgent && isImportant) quadrant = 2; // Schedule  
    else if (isUrgent && !isImportant) quadrant = 3; // Delegate
    else quadrant = 4;                                // Delete

    // ADHD-specific adjustments
    const adhdFactors = this.calculateADHDFactors(todo, quadrant);
    
    // Apply ADHD optimizations
    if (this.config.adhdOptimizations.overwhelmPrevention && quadrant === 1) {
      // If confidence is low and it's Q1, consider moving to Q2 to prevent overwhelm
      const avgConfidence = (urgencyScore.confidence + importanceScore.confidence) / 2;
      if (avgConfidence < 70) {
        quadrant = 2;
      }
    }

    const confidence = Math.min(
      (urgencyScore.confidence + importanceScore.confidence) / 2,
      100
    );

    const reasoning = [
      ...urgencyScore.reasoning,
      ...importanceScore.reasoning
    ];

    return {
      urgencyScore: urgencyScore.score,
      importanceScore: importanceScore.score,
      eisenhowerQuadrant: quadrant,
      confidence,
      reasoning,
      adhdFactors
    };
  }

  private calculateUrgencyScore(
    todo: TodoItem, 
    projects?: Project[], 
    currentTime: Date = new Date()
  ): { score: number; confidence: number; reasoning: string[] } {
    let score = 0;
    let confidence = 0;
    const reasoning: string[] = [];

    // Factor 1: Due date proximity
    if (todo.due_date) {
      const dueDate = new Date(todo.due_date);
      const hoursUntilDue = (dueDate.getTime() - currentTime.getTime()) / (1000 * 60 * 60);
      
      if (hoursUntilDue < 0) {
        score += 10 * this.config.urgencyFactors.dueDateProximity;
        reasoning.push('Task is overdue');
        confidence += 95;
      } else if (hoursUntilDue <= 24) {
        score += 9 * this.config.urgencyFactors.dueDateProximity;
        reasoning.push('Due within 24 hours');
        confidence += 90;
      } else if (hoursUntilDue <= 48) {
        score += 8 * this.config.urgencyFactors.dueDateProximity;
        reasoning.push('Due within 48 hours');
        confidence += 85;
      } else if (hoursUntilDue <= 168) { // 1 week
        score += 6 * this.config.urgencyFactors.dueDateProximity;
        reasoning.push('Due this week');
        confidence += 70;
      } else {
        score += 3 * this.config.urgencyFactors.dueDateProximity;
        reasoning.push('Due date is far out');
        confidence += 60;
      }
    } else {
      // No due date - moderate urgency
      score += 4 * this.config.urgencyFactors.dueDateProximity;
      confidence += 40;
    }

    // Factor 2: Urgency keywords in title/description
    const text = `${todo.title} ${todo.description || ''}`.toLowerCase();
    let keywordScore = 0;
    let foundUrgencyKeywords = 0;

    for (const [keyword, intensity] of Object.entries(this.urgencyKeywords)) {
      if (text.includes(keyword.toLowerCase())) {
        keywordScore += intensity;
        foundUrgencyKeywords++;
        reasoning.push(`Contains urgency keyword: "${keyword}"`);
      }
    }

    if (foundUrgencyKeywords > 0) {
      score += (keywordScore / foundUrgencyKeywords) * this.config.urgencyFactors.keywordIntensity;
      confidence += Math.min(foundUrgencyKeywords * 20, 80);
    }

    // Factor 3: Time of creation (late night = might be urgent stress)
    const creationHour = new Date(todo.created_at).getHours();
    if (creationHour >= 22 || creationHour <= 6) {
      score += 7 * this.config.urgencyFactors.timeOfCreation;
      reasoning.push('Created during off-hours (possible urgent situation)');
      confidence += 50;
    }

    // Factor 4: Project deadline proximity
    if (todo.project_id && projects) {
      const project = projects.find(p => p.id === todo.project_id);
      if (project && project.deadline) {
        const projectDue = new Date(project.deadline);
        const hoursUntilProjectDue = (projectDue.getTime() - currentTime.getTime()) / (1000 * 60 * 60);
        
        if (hoursUntilProjectDue <= 72) { // 3 days
          score += 8 * this.config.urgencyFactors.projectDeadline;
          reasoning.push(`Project "${project.name}" deadline is soon`);
          confidence += 75;
        } else if (hoursUntilProjectDue <= 168) { // 1 week
          score += 6 * this.config.urgencyFactors.projectDeadline;
          reasoning.push(`Project "${project.name}" deadline approaching`);
          confidence += 65;
        }
      }
    }

    // ADHD-specific time blindness compensation
    if (this.config.adhdOptimizations.timeBlindnessCompensation) {
      score *= 1.2; // Slightly boost urgency scores to compensate for time blindness
    }

    return {
      score: Math.min(score, 10),
      confidence: Math.min(confidence / 4, 100), // Average confidence across factors
      reasoning
    };
  }

  private calculateImportanceScore(
    todo: TodoItem, 
    projects?: Project[]
  ): { score: number; confidence: number; reasoning: string[] } {
    let score = 0;
    let confidence = 0;
    const reasoning: string[] = [];

    const text = `${todo.title} ${todo.description || ''}`.toLowerCase();

    // Factor 1: Project impact
    if (todo.project_id && projects) {
      const project = projects.find(p => p.id === todo.project_id);
      if (project) {
        // Larger projects or projects with deadlines are more important
        const projectTodoCount = projects.reduce((count, p) => count + p.todos.length, 0);
        if (projectTodoCount > 10) {
          score += 7 * this.config.importanceFactors.projectImpact;
          reasoning.push(`Part of large project: "${project.name}"`);
          confidence += 70;
        } else if (projectTodoCount > 5) {
          score += 6 * this.config.importanceFactors.projectImpact;
          reasoning.push(`Part of medium project: "${project.name}"`);
          confidence += 60;
        } else {
          score += 5 * this.config.importanceFactors.projectImpact;
          reasoning.push(`Part of project: "${project.name}"`);
          confidence += 50;
        }
      }
    } else {
      score += 4 * this.config.importanceFactors.projectImpact;
      confidence += 30;
    }

    // Factor 2: Stakeholder keywords
    let stakeholderScore = 0;
    let foundStakeholderKeywords = 0;

    for (const [keyword, impact] of Object.entries(this.stakeholderKeywords)) {
      if (text.includes(keyword.toLowerCase())) {
        stakeholderScore += impact;
        foundStakeholderKeywords++;
        reasoning.push(`Involves stakeholder activity: "${keyword}"`);
      }
    }

    if (foundStakeholderKeywords > 0) {
      score += (stakeholderScore / foundStakeholderKeywords) * this.config.importanceFactors.stakeholderKeywords;
      confidence += Math.min(foundStakeholderKeywords * 25, 80);
    }

    // Factor 3: Long-term goal keywords
    let longTermScore = 0;
    let foundLongTermKeywords = 0;

    for (const [keyword, impact] of Object.entries(this.longTermKeywords)) {
      if (text.includes(keyword.toLowerCase())) {
        longTermScore += impact;
        foundLongTermKeywords++;
        reasoning.push(`Relates to long-term goals: "${keyword}"`);
      }
    }

    if (foundLongTermKeywords > 0) {
      score += (longTermScore / foundLongTermKeywords) * this.config.importanceFactors.longTermGoals;
      confidence += Math.min(foundLongTermKeywords * 20, 70);
    }

    // Factor 4: Task complexity (more complex = potentially more important)
    let complexityScore = 0;
    let foundComplexityKeywords = 0;

    for (const [keyword, complexity] of Object.entries(this.complexityKeywords)) {
      if (text.includes(keyword.toLowerCase())) {
        complexityScore += complexity;
        foundComplexityKeywords++;
      }
    }

    if (foundComplexityKeywords > 0) {
      const avgComplexity = complexityScore / foundComplexityKeywords;
      if (avgComplexity >= 7) {
        score += 7 * this.config.importanceFactors.complexitySignals;
        reasoning.push('High-complexity task (likely important)');
        confidence += 60;
      } else if (avgComplexity >= 5) {
        score += 5 * this.config.importanceFactors.complexitySignals;
        reasoning.push('Medium-complexity task');
        confidence += 50;
      } else {
        score += 3 * this.config.importanceFactors.complexitySignals;
        reasoning.push('Low-complexity task');
        confidence += 40;
      }
    }

    return {
      score: Math.min(score, 10),
      confidence: Math.min(confidence / 4, 100),
      reasoning
    };
  }

  private calculateADHDFactors(todo: TodoItem, quadrant: number): PriorityAnalysis['adhdFactors'] {
    const text = `${todo.title} ${todo.description || ''}`.toLowerCase();
    
    // Calculate required energy level based on complexity
    let complexityScore = 0;
    let foundComplexity = 0;

    for (const [keyword, complexity] of Object.entries(this.complexityKeywords)) {
      if (text.includes(keyword.toLowerCase())) {
        complexityScore += complexity;
        foundComplexity++;
      }
    }

    const avgComplexity = foundComplexity > 0 ? complexityScore / foundComplexity : 5;
    
    let energyLevel: 'low' | 'medium' | 'high';
    if (avgComplexity >= 8) energyLevel = 'high';
    else if (avgComplexity >= 5) energyLevel = 'medium';
    else energyLevel = 'low';

    // Calculate context switching cost
    let contextSwitchingCost = 0;
    if (todo.project_id) contextSwitchingCost += 1; // Project context
    if (todo.tags.length > 0) contextSwitchingCost += todo.tags.length * 0.5;
    if (avgComplexity >= 7) contextSwitchingCost += 2; // Complex tasks require more context

    // Determine hyperfocus compatibility
    const hyperfocusCompatible = 
      avgComplexity >= 6 && // Interesting enough
      (todo.estimated_minutes || 60) >= 25 && // Long enough for hyperfocus
      quadrant !== 4; // Not a "delete" task

    return {
      energyLevel,
      contextSwitchingCost: Math.min(contextSwitchingCost, 10),
      hyperfocusCompatible
    };
  }

  /**
   * Get quadrant name and description
   */
  public getQuadrantInfo(quadrant: 1 | 2 | 3 | 4): { name: string; description: string; color: string } {
    switch (quadrant) {
      case 1:
        return {
          name: 'Do First',
          description: 'Urgent and Important - Handle immediately',
          color: '#ef4444' // Red
        };
      case 2:
        return {
          name: 'Schedule',
          description: 'Important but Not Urgent - Plan and schedule',
          color: '#3b82f6' // Blue  
        };
      case 3:
        return {
          name: 'Delegate',
          description: 'Urgent but Not Important - Consider delegating',
          color: '#f59e0b' // Amber
        };
      case 4:
        return {
          name: 'Delete',
          description: 'Neither Urgent nor Important - Eliminate if possible',
          color: '#6b7280' // Gray
        };
    }
  }

  /**
   * Batch analyze multiple todos with ADHD-optimized grouping
   */
  public batchAnalyze(
    todos: TodoItem[],
    projects?: Project[],
    currentTime: Date = new Date()
  ): (TodoItem & { priorityAnalysis: PriorityAnalysis })[] {
    const analyzed = todos.map(todo => ({
      ...todo,
      priorityAnalysis: this.analyzePriority(todo, projects, currentTime)
    }));

    // ADHD optimization: prevent overwhelm by limiting Q1 tasks
    if (this.config.adhdOptimizations.overwhelmPrevention) {
      const q1Tasks = analyzed.filter(t => t.priorityAnalysis.eisenhowerQuadrant === 1);
      
      if (q1Tasks.length > 5) {
        // Sort Q1 tasks by confidence and keep top 5, move others to Q2
        const sortedQ1 = q1Tasks.sort((a, b) => b.priorityAnalysis.confidence - a.priorityAnalysis.confidence);
        
        sortedQ1.slice(5).forEach(task => {
          task.priorityAnalysis.eisenhowerQuadrant = 2;
          task.priorityAnalysis.reasoning.push('Moved to Schedule to prevent ADHD overwhelm');
        });
      }
    }

    return analyzed;
  }

  /**
   * Get ADHD-optimized task recommendations based on current context
   */
  public getADHDRecommendations(
    analyzedTodos: (TodoItem & { priorityAnalysis: PriorityAnalysis })[],
    context: {
      availableTimeMinutes?: number;
      currentEnergyLevel?: 'low' | 'medium' | 'high';
      preferHyperfocus?: boolean;
    }
  ): (TodoItem & { priorityAnalysis: PriorityAnalysis })[] {
    let recommendations = analyzedTodos.filter(t => t.status === 'pending');

    // Filter by energy level
    if (context.currentEnergyLevel) {
      recommendations = recommendations.filter(task => {
        const requiredEnergy = task.priorityAnalysis.adhdFactors.energyLevel;
        
        if (context.currentEnergyLevel === 'low') {
          return requiredEnergy === 'low';
        } else if (context.currentEnergyLevel === 'medium') {
          return requiredEnergy === 'low' || requiredEnergy === 'medium';
        } else {
          return true; // High energy can handle any task
        }
      });
    }

    // Filter by available time
    if (context.availableTimeMinutes) {
      recommendations = recommendations.filter(task => {
        const estimatedTime = task.estimated_minutes || 30; // Default estimate
        return estimatedTime <= context.availableTimeMinutes!;
      });
    }

    // Filter for hyperfocus if requested
    if (context.preferHyperfocus) {
      recommendations = recommendations.filter(task => 
        task.priorityAnalysis.adhdFactors.hyperfocusCompatible
      );
    }

    // Sort by quadrant priority, then by confidence
    return recommendations.sort((a, b) => {
      if (a.priorityAnalysis.eisenhowerQuadrant !== b.priorityAnalysis.eisenhowerQuadrant) {
        return a.priorityAnalysis.eisenhowerQuadrant - b.priorityAnalysis.eisenhowerQuadrant;
      }
      return b.priorityAnalysis.confidence - a.priorityAnalysis.confidence;
    });
  }
}